import { Button } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { RefreshOutline, TrashOutline } from '../../../../../../../icons/icons';
import { ThresholdsCompanyModeOptions } from '../../../../../../../types/dropdown-options';
import { CompanyThresholdsMode } from '../../../../../../../types/thresholds';
import { DropdownListWithValueField } from '../../../../../../components/inputs/DropdownListWithValueField';
import { useAppSelector } from '../../../../../../hooks/store-hooks';
import React from 'react';

export const ThresholdHeaderBar = React.memo(
    (props: {
        tab: 'company' | 'user';
        companyMode: CompanyThresholdsMode;
        hideClearAll?: boolean;
        hideRestoreDefaults?: boolean;
        onRestoreDefaultClicked?: () => void;
        onClearClicked?: () => void;
        onChangeTab?: (tab: 'user' | 'company') => void;
        onChangeCompanyMode?: (mode: CompanyThresholdsMode) => void;
    }) => {
        const { tab, companyMode, onChangeTab, onChangeCompanyMode } = props;
        const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);
        const canEditCompanyThresholds = useAppSelector((x) => x.user.rights.canEditCompanyThresholds);

        return (
            <div className="d-flex justify-content-between align-items-center gap-2 pe-2 border-bottom">
                {companyFeaturesEnabled && (
                    <TabStrip
                        tabContentStyle={{ display: 'none' }}
                        selected={tab === 'user' ? 0 : 1}
                        onSelect={(e) => onChangeTab?.(e.selected === 0 ? 'user' : 'company')}>
                        <TabStripTab title={'User'}></TabStripTab>
                        <TabStripTab title={'Company'}></TabStripTab>
                    </TabStrip>
                )}
                <div className="flex-grow-1"></div>

                {companyFeaturesEnabled && (
                    <>
                        {tab === 'company' ? (
                            <div className="d-flex gap-2 align-items-center pe-2">
                                <span>Mode:</span>
                                <DropdownListWithValueField
                                    disabled={!canEditCompanyThresholds}
                                    value={companyMode}
                                    data={ThresholdsCompanyModeOptions}
                                    onChange={(e) => onChangeCompanyMode?.(e.value)}
                                />
                            </div>
                        ) : (
                            <div className="pe-2 d-flex flex-column secondary-text align-items-end">
                                <div>
                                    <span>Mode: </span>
                                    <span className="fw-bold">
                                        {ThresholdsCompanyModeOptions.find((x) => x.value === companyMode)?.text}
                                    </span>
                                    <span>.</span>
                                </div>
                                <div>
                                    {companyMode === CompanyThresholdsMode.specified ? (
                                        <span>Only company values are in effect.</span>
                                    ) : companyMode === CompanyThresholdsMode.notUsed ? (
                                        <span>Only user values are in effect.</span>
                                    ) : companyMode === CompanyThresholdsMode.minimum ? (
                                        <span>Company enforces minimum values.</span>
                                    ) : companyMode === CompanyThresholdsMode.template ? (
                                        <span>Company values are a template. Free to modify values.</span>
                                    ) : (
                                        <span></span>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div className="d-flex gap-2 py-2">
                    {!props.hideRestoreDefaults && (
                        <Button
                            fillMode={'outline'}
                            svgIcon={RefreshOutline}
                            title="Restore defaults"
                            onClick={props.onRestoreDefaultClicked}
                        />
                    )}
                    {!props.hideClearAll && (
                        <Button fillMode={'outline'} svgIcon={TrashOutline} title="Clear page" onClick={props.onClearClicked} />
                    )}
                </div>
            </div>
        );
    }
);
